import type {Poll} from "@/hooks/useGetUserPolls";

/**
 * Minute-qset major versions the client can route to. v3 is the Lit widget
 * library, v4 the React port; both ship in every bundle so a device pinned to
 * one version can still open the other's URL.
 */
export const QSET_VERSIONS = [3, 4] as const;

export type QsetVersion = (typeof QSET_VERSIONS)[number];

export const DEFAULT_QSET_VERSION: QsetVersion = 3;

export const POLL_V3_BASE_PATH = "/poll1";
export const POLL_V4_BASE_PATH = "/poll2";

export type PollBasePath = typeof POLL_V3_BASE_PATH | typeof POLL_V4_BASE_PATH;

const isQsetVersion = (value: number): value is QsetVersion =>
  (QSET_VERSIONS as readonly number[]).includes(value);

/**
 * Normalises the qset version the client was handed. The value reaches the app
 * through the `qset-version` meta tag in `index.html`, written per build by the
 * Cloudflare split pipeline. Anything unrecognised -- a missing tag included --
 * falls back to v3, so a typo in the CI matrix degrades to the production-proven
 * version rather than shipping an unintended rollout.
 */
export const parseQsetVersion = (raw: unknown): QsetVersion => {
  const parsed = Number(raw);
  if (!raw || Number.isNaN(parsed) || !isQsetVersion(parsed)) {
    return DEFAULT_QSET_VERSION;
  }
  return parsed;
};

/**
 * With `introsetFollowsSplit` (`Config.INTROSET_FOLLOWS_SPLIT`) on, introsets
 * follow the arm like classic polls; off keeps them on v3. It is a kill switch,
 * not a hard limitation: `Poll2Page` renders an introset if it is handed one,
 * and `usePatchResult` writes the introset result path for both pages. v4 has
 * no `flow-*` or `quiz-*` widgets, so an introset that gains one would render
 * placeholders on the v4 arm; off puts every introset back on v3 with one
 * build. It defaults to off so a caller that forgets it keeps the proven arm.
 *
 * `/poll2/<introset-slug>` reached directly renders on v4 whatever the switch
 * says, which is how to exercise an introset on v4 while it is off.
 *
 * `isPollKnown` is false when the slug is not in the user's poll list at all —
 * an expired or re-assigned poll, or a stale shared link. That is different
 * from a known poll whose optional `type` field is absent, which is a classic
 * poll: here we know nothing, and defaulting to the build flag would send an
 * introset to v4. v3 serves every poll type, so it is the safe answer.
 */
export const resolvePollBasePath = ({
  qsetVersion,
  pollType,
  isPollKnown = true,
  introsetFollowsSplit = false,
}: {
  qsetVersion: QsetVersion;
  pollType: Poll["type"];
  isPollKnown?: boolean;
  introsetFollowsSplit?: boolean;
}): PollBasePath => {
  if (!isPollKnown) return POLL_V3_BASE_PATH;
  if (pollType === "introset" && !introsetFollowsSplit) {
    return POLL_V3_BASE_PATH;
  }
  return qsetVersion === 4 ? POLL_V4_BASE_PATH : POLL_V3_BASE_PATH;
};

/**
 * Which version is actually on screen, read back from the URL. Reported with
 * feedback and bug reports in preference to `QSET_VERSION`, because the build
 * flag can lie: a v4 build still serves some polls from `/poll1` (see
 * `resolvePollBasePath`).
 *
 * Returns undefined off the survey routes, where no version applies.
 */
export const qsetVersionFromPath = (
  pathname: string,
): QsetVersion | undefined => {
  const [, basePath] = pathname.split("/");
  if (basePath === "poll1") return 3;
  if (basePath === "poll2") return 4;
  return undefined;
};

export const resolvePollPath = ({
  pollSlug,
  ...rest
}: {pollSlug: string} & Parameters<typeof resolvePollBasePath>[0]): string =>
  `${resolvePollBasePath(rest)}/${pollSlug}`;
